import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { formatDate } from '../../../../utils/Utils';
import useTutorialChapters from '../../../system/tutorials/useTutorialChapters';
import companyTutorialsSummary from './companyTutorialsSummary';

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {children}
    </Typography>
  );
}

function UserRow({ user, totalLessons }) {
  const detail = user.opened === 0
    ? 'Ainda não abriu'
    : `${user.completed} de ${totalLessons} assistidas · ${fmtDate(user.lastWatchedAt)}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: user.opened ? '#43A047' : '#BDBDBD', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{user.name}</Typography>
        {user.currentChapter && (
          <Typography variant="caption" color="text.secondary">Último capítulo: {user.currentChapter}</Typography>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>{detail}</Typography>
    </Box>
  );
}

function LessonLines({ items, renderValue }) {
  return items.slice(0, 5).map((item) => (
    <Box key={item.lessonId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.primary' }}>{item.title}</Typography>
      {renderValue && <Typography variant="body2" color="text.secondary">{renderValue(item)}</Typography>}
    </Box>
  ));
}

/**
 * CompanyTutorialsCard - Card "Tutoriais" da página da empresa (Admin Geral)
 *
 * Mostra se a equipe está usando os tutoriais: lições assistidas, quem assistiu, o que foi
 * aberto pelo atalho "Como usar esta tela" (onde a equipe travou) e as respostas de
 * "Esta aula ajudou?". Os dados vêm de `tutorials` (admin/companies/get.php), cruzados com
 * tutorials.json pelo id fixo de cada lição.
 *
 * @param {Object} props
 * @param {Array} props.tutorials - linhas de progresso da empresa
 * @param {Array} props.users - usuários da empresa
 */
function CompanyTutorialsCard({ tutorials, users }) {
  const { chapters, loading } = useTutorialChapters();
  if (loading || chapters.length === 0) return null;

  const summary = companyTutorialsSummary(tutorials || [], chapters, users || []);
  const inUse = summary.usersWatching > 0;

  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', color: '#5A80FB' }}>
          <SchoolOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
          Tutoriais
        </Typography>
        <Chip
          size="small"
          label={inUse ? 'Em uso' : 'Não usou'}
          sx={{
            backgroundColor: inUse ? '#E8F5E9' : '#F5F5F5',
            color: inUse ? '#2E7D32' : '#616161',
            fontWeight: 500,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <InfoRow label="Lições assistidas pela equipe" value={`${summary.completedLessons} de ${summary.totalLessons}`} />
          <InfoRow label="Pessoas que abriram" value={`${summary.usersWatching} de ${summary.perUser.length}`} />
          <InfoRow label="Último acesso" value={fmtDate(summary.lastWatchedAt)} />
          <InfoRow label="Aberturas pela ajuda da tela" value={summary.helpViews} />
          <InfoRow label="Esta aula ajudou?" value={`${summary.helpfulYes} sim · ${summary.helpfulNo} não`} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <SectionTitle>Por pessoa</SectionTitle>
          {summary.perUser.map((user) => <UserRow key={user.id} user={user} totalLessons={summary.totalLessons} />)}
        </Box>
      </Box>

      {(summary.fromHelp.length > 0 || summary.notHelpful.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <SectionTitle>Mais abertas pela ajuda da tela</SectionTitle>
            {summary.fromHelp.length > 0
              ? <LessonLines items={summary.fromHelp} renderValue={(item) => `${item.views}x`} />
              : <Typography variant="body2" color="text.secondary">Nenhuma ainda.</Typography>}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <SectionTitle>Marcadas como "não ajudou"</SectionTitle>
            {summary.notHelpful.length > 0
              ? <LessonLines items={summary.notHelpful} />
              : <Typography variant="body2" color="text.secondary">Nenhuma.</Typography>}
          </Box>
        </Box>
      )}
    </Card>
  );
}

export default CompanyTutorialsCard;
